import { useQuery } from '@tanstack/react-query';
import { Gauge } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PRICING_PLANS } from './pricingPlans.config';
import { QueryKey } from '@/Config/api/queryKey';
import { SubscriptionDto } from '@/Domain/api/Subscription/dto/Subscription.dto';
import { Usage } from '@/Domain/api/Subscription/dto/SubscriptionUsage.dto';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';

const NUDGE_THRESHOLD = 0.8;
const MONTHS_PER_YEAR = 12;

type Props = {
  currentPlan: SubscriptionDto['current_plan'];
};

export const PlanUsageNudge = ({ currentPlan }: Props) => {
  const { t } = useTranslation('subscription_page');
  const { data: usage } = useQuery<Usage>({ queryKey: [QueryKey.subscription.usage] });

  if (!usage || usage.messages_limit <= 0) return null;
  const ratio = usage.messages_used / usage.messages_limit;
  if (ratio < NUDGE_THRESHOLD) return null;

  // An annual allowance is twelve monthly ones; compare like with like before promising more.
  const monthlyAllowance =
    currentPlan.interval === SubscriptionIntervalEnum.Annually
      ? usage.messages_limit / MONTHS_PER_YEAR
      : usage.messages_limit;
  const hasPlanWithMoreCredits = PRICING_PLANS.some(
    (plan) => plan.monthlyCredits !== undefined && plan.monthlyCredits > monthlyAllowance,
  );
  if (!hasPlanWithMoreCredits) return null;

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3"
      data-testid="pricing-usage-nudge"
    >
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-warning-muted text-warning">
        <Gauge className="size-4" aria-hidden="true" />
      </span>
      <p className="text-sm text-foreground">
        {ratio >= 1
          ? t('pricing.usage_nudge.all_used')
          : t('pricing.usage_nudge.high', { percent: Math.floor(ratio * 100) })}
      </p>
    </div>
  );
};
